/**
 * Utility to map dispute types to their workflow stages.
 * This provides the connection between Settings > Dispute Types and the Dispute Details workflow tab.
 *
 * NOTE: This is now a fallback-only utility for pre-migration disputes that don't have
 * snapshotted workflow stages (DisputeWorkflowStages table). New disputes snapshot their
 * stages at creation time via the backend. Callers should prefer snapshot data from
 * useDisputeWorkflowStages() and only fall back to these hardcoded mappings when no
 * snapshot is available.
 */

export interface WorkflowStage {
  level: number;
  title: string;
  description: string;
  estimatedDays?: number;
}

export interface DisputeTypeWorkflow {
  disputeType: string;
  workflowName: string;
  stages: WorkflowStage[];
}

/**
 * Mock data representing the workflow configurations from Settings > Dispute Types
 * In a real application, this would be fetched from the backend API
 */
export const disputeTypeWorkflows: Record<string, DisputeTypeWorkflow> = {
  'Billing Error': {
    disputeType: 'Billing Error',
    workflowName: 'Billing Error Resolution Workflow',
    stages: [
      {
        level: 1,
        title: 'Initial Review & Categorization',
        description: 'Dispute received and automatically categorized by AI',
        estimatedDays: 2,
      },
      {
        level: 2,
        title: 'Investigation & Evidence Gathering',
        description: 'Collecting relevant documents and customer communications',
        estimatedDays: 3,
      },
      {
        level: 3,
        title: 'Final Resolution Notice',
        description: 'Resolution approved and customer notified',
        estimatedDays: 2,
      },
    ],
  },
  'Service Quality': {
    disputeType: 'Service Quality',
    workflowName: 'Service Quality Dispute Workflow',
    stages: [
      {
        level: 1,
        title: 'Initial Review & Categorization',
        description: 'Dispute received and service quality issues documented',
        estimatedDays: 3,
      },
      {
        level: 2,
        title: 'Investigation & Evidence Gathering',
        description: 'Collecting service delivery records and customer feedback',
        estimatedDays: 5,
      },
      {
        level: 3,
        title: 'Resolution Recommendation',
        description: 'Service team reviews and proposes resolution',
        estimatedDays: 3,
      },
      {
        level: 4,
        title: 'Final Approval & Closure',
        description: 'Management approval and customer notification',
        estimatedDays: 2,
      },
    ],
  },
  'Payment Dispute': {
    disputeType: 'Payment Dispute',
    workflowName: 'Payment Dispute Resolution Workflow',
    stages: [
      {
        level: 1,
        title: 'Payment Verification',
        description: 'Verify payment records and allocation',
        estimatedDays: 1,
      },
      {
        level: 2,
        title: 'Final Resolution Notice',
        description: 'Correction applied and customer notified',
        estimatedDays: 2,
      },
    ],
  },
  'Contract Terms': {
    disputeType: 'Contract Terms',
    workflowName: 'Contract Terms Dispute Workflow',
    stages: [
      {
        level: 1,
        title: 'Initial Review & Documentation',
        description: 'Contract terms reviewed and documented',
        estimatedDays: 3,
      },
      {
        level: 2,
        title: 'Legal Review',
        description: 'Legal team reviews contract interpretation',
        estimatedDays: 5,
      },
      {
        level: 3,
        title: 'Negotiation & Resolution',
        description: 'Negotiate terms with customer',
        estimatedDays: 4,
      },
      {
        level: 4,
        title: 'Management Approval',
        description: 'Senior management reviews and approves resolution',
        estimatedDays: 3,
      },
      {
        level: 5,
        title: 'Final Documentation & Closure',
        description: 'Contract amendments documented and finalized',
        estimatedDays: 2,
      },
    ],
  },
  'Duplicate Charge': {
    disputeType: 'Duplicate Charge',
    workflowName: 'Duplicate Charge Resolution Workflow',
    stages: [
      {
        level: 1,
        title: 'Charge Verification',
        description: 'Verify duplicate charges in billing system',
        estimatedDays: 1,
      },
      {
        level: 2,
        title: 'Quick Resolution',
        description: 'Credit applied and customer notified',
        estimatedDays: 1,
      },
    ],
  },
  'Product Defect': {
    disputeType: 'Product Defect',
    workflowName: 'Product Defect Dispute Workflow',
    stages: [
      {
        level: 1,
        title: 'Initial Review & Documentation',
        description: 'Product defect documented with evidence',
        estimatedDays: 3,
      },
      {
        level: 2,
        title: 'Quality Assurance Review',
        description: 'QA team investigates product defect claims',
        estimatedDays: 5,
      },
      {
        level: 3,
        title: 'Final Resolution Notice',
        description: 'Replacement or refund processed',
        estimatedDays: 3,
      },
    ],
  },
};

/**
 * Get workflow stages for a specific dispute type
 * @param disputeType - The dispute type name (e.g., "Billing Error")
 * @returns The workflow configuration for that dispute type, or a default workflow if not found
 */
export function getWorkflowForDisputeType(disputeType: string): DisputeTypeWorkflow {
  // Return the specific workflow if it exists
  if (disputeTypeWorkflows[disputeType]) {
    return disputeTypeWorkflows[disputeType];
  }

  // Return a default workflow for unknown dispute types
  return {
    disputeType,
    workflowName: `${disputeType} Workflow`,
    stages: [
      {
        level: 1,
        title: 'Initial Review & Categorization',
        description: 'Dispute received and automatically categorized by AI',
        estimatedDays: 3,
      },
      {
        level: 2,
        title: 'Investigation & Evidence Gathering',
        description: 'Collecting relevant documents and customer communications',
        estimatedDays: 5,
      },
      {
        level: 3,
        title: 'Resolution Recommendation',
        description: 'AI generates resolution recommendation based on analysis',
        estimatedDays: 3,
      },
      {
        level: 4,
        title: 'Final Approval & Closure',
        description: 'Management approval and customer notification',
        estimatedDays: 2,
      },
    ],
  };
}

/**
 * Get all available dispute types with their workflows
 * @returns Array of all dispute type workflows
 */
export function getAllDisputeTypeWorkflows(): DisputeTypeWorkflow[] {
  return Object.values(disputeTypeWorkflows);
}
